import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { forkJoin, fromEvent, merge, Observable, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";

import { AssetVariantGallery } from "@hermes/aphrodite/image-variants-gallery";
import { LayoutFacade } from "@hermes/aphrodite/layout";
import { Appareance, BORDER_RADIUS, SVG_ONLY } from "@hermes/aphrodite/tag";
import {
    ControlPosition,
    HIGH_POSITION,
    LOW_POSITION,
    MEDIUM_POSITION,
} from "@hermes/aphrodite/video";
import { Asset } from "@hermes/api-model-asset";
import { Product } from "@hermes/api-model-product";

import { Context, LOCALE, WINDOW } from "@hermes/app-core";
import {
    HERO_PRODUCT,
    ImageResizerComponent,
    MediaType,
    PRODUCT_IN_GRID,
} from "@hermes/fragments/image-resizer";
import { QuickBuyProductInfos } from "@hermes/fragments/quick-buy-modal";
import { Locale } from "@hermes/locale";
import { DISPLAY_ONLY, FeatureFlagFacade } from "@hermes/states/flipper";
import {
    GridFacade,
    GridProduct,
    hasStockRetailOnly,
    GridMenuLevel,
    getPageSizeValue,
    PageSizeValue,
    ProductLoaded,
    GridAsset,
    skuToGridProductId,
    getGridTitle,
} from "@hermes/states/grid-filters-state";

import { generateLocalizedHref } from "@hermes/utils/helpers";
import { AssetMediaType } from "@hermes/utils-generic/constants";

import { skuToId, stopEventPropagation } from "@hermes/utils-generic/helpers";

import { ProductInViewportService } from "../../services/product-in-viewport-service/product-in-viewport.service";

const MAX_PRODUCTS_VISIBLE_IN_FIRST_THREE_ROWS = 15;

/**
 * Result item component displaying the product in front
 */
@Component({
    selector: "h-grid-result-item",
    templateUrl: "./grid-result-item.component.html",
    styleUrls: ["./grid-result-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridResultItemComponent
    implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
    // direct access to html node element on html component template
    @ViewChild("productItem", { static: true, read: ElementRef })
    public productItem!: ElementRef<HTMLDivElement>;

    @ViewChild("productItemMeta", { static: true, read: ElementRef })
    public productItemMeta!: ElementRef;

    @ViewChild("informationOfProduct", { static: true, read: ElementRef })
    public informations!: ElementRef<HTMLDivElement>;

    @ViewChild("imageResizerDivContainer", { static: true, read: ElementRef })
    public imageResizerDivContainer!: ElementRef<HTMLDivElement>;
    @ViewChild("imageResizerContainer", { read: ViewContainerRef })
    public imageResizerContainer!: ViewContainerRef;
    @ViewChild("imageResizer", { read: TemplateRef })
    public imageResizer!: TemplateRef<ImageResizerComponent>;

    @Input()
    public isSearchGrid!: boolean;

    @Input()
    public index!: number;

    @Input()
    public isListMode: boolean = false;

    @Input()
    public isMobile: boolean = false;

    @Input()
    public editosDisplayed: number = 0;

    @Input()
    public gridChange$!: Observable<void>;

    @Input()
    public isVtoDisplayed: boolean = false;

    public productInGrid: MediaType = PRODUCT_IN_GRID;
    public heroProduct: MediaType = HERO_PRODUCT;
    public borderRadius: Appareance = BORDER_RADIUS;
    public svgOnly: Appareance = SVG_ONLY;
    public urlRedirect: string = "";
    public readonly numberOfImageDisplayed = 3;

    /** Allows to control the display on chevrons on each grid item on desktop */
    public showAlternativesViewsOnDesktop: boolean = false;

    /** Allows to control the display of carousel indicators (dots) on each grid item on Mobile */
    public showAlternativesViewsOnMobile = false;

    public shouldShowAlternativeViews = false;
    public readonly gridAssetsVariantsParameters: string =
        "wid=10&hei=10&$product_item_grid_g$";
    public carouselInViewPort: boolean = false;

    public highPosition: ControlPosition = HIGH_POSITION;

    public lowPosition: ControlPosition = LOW_POSITION;

    public shouldDisplayTagDisplayOnly$: Observable<boolean> =
        this.featureFlagFacade
            .isActivated(DISPLAY_ONLY)
            .pipe(
                map(
                    (activated) =>
                        activated &&
                        this.product?.stock?.hasVariantInEcomStock === false &&
                        this.product?.stock?.displayOnly === true,
                ),
            );

    public controlPosition: ControlPosition = MEDIUM_POSITION;
    public id!: string;
    public gridProductId!: string;
    public hasVideo: boolean = false;
    public isRetailOnlyStock: boolean = false;
    public firstAsset!: string;
    public assetsInterface!: Asset[];
    public elementRefForQuickBuy?: ElementRef;
    public quickBuyProductInfos?: QuickBuyProductInfos;

    public title!: string;

    /**
     * This flag is used to disable events on images to trigger the product link in background.
     */
    public blockPointer = false;

    private subscription: Subscription = new Subscription();
    private menuLevel!: GridMenuLevel;

    private prProduct!: GridProduct;

    constructor(
        @Inject(LOCALE) private locale: Locale,
        @Inject(WINDOW) private window: Window,
        private gridFacade: GridFacade,
        private productInViewportService: ProductInViewportService,
        private changeDetector: ChangeDetectorRef,
        private featureFlagFacade: FeatureFlagFacade,
        private context: Context,
        public layoutFacade: LayoutFacade,
    ) {}

    public get assetVariantsGallery(): AssetVariantGallery[] {
        return this.prProduct.gridAssetsVariants.map(
            (gridAsset: GridAsset) => ({
                url: gridAsset.url,
                codeRgb: gridAsset.codeRgb,
                title: gridAsset.title,
                isBrokenImage: false,
            }),
        );
    }

    public get isBrowserMode(): boolean {
        return this.context.isInBrowserMode();
    }

    public get hasMoreColors(): boolean {
        return this.product.productSetType?.startsWith("BELTKIT");
    }

    public get product() {
        return this.prProduct;
    }

    @Input()
    public set product(product: GridProduct) {
        this.prProduct = product;
        this.title = getGridTitle(product);
        if (product.quickBuyProperties?.eligible) {
            this.quickBuyProductInfos = {
                product: product as unknown as Product,
                variants: product.quickBuyProperties.variants,
                variationType: product.quickBuyProperties.variationType,
            };
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes["isListMode"]?.currentValue !==
                changes["isListMode"]?.previousValue ||
            changes["product"]?.currentValue !==
                changes["product"]?.previousValue ||
            changes["isMobile"]?.currentValue !==
                changes["isMobile"]?.previousValue
        ) {
            this.showAlternativesViewsOnMobile =
                this.product.assets !== undefined &&
                this.product.assets.length > 1 &&
                ((this.isMobile && this.isListMode) ||
                    (!this.isListMode && this.isMobile));
            this.controlPosition = this.getControlPosition();
            this.shouldShowAlternativeViews =
                this.shouldDisplayAlternativeViews();
            this.hasVideo = this.hasVideoAsset();
            this.changeDetector.detectChanges();
        }
    }

    public ngOnInit(): void {
        this.elementRefForQuickBuy = this.productItem;
        this.urlRedirect = generateLocalizedHref(this.product.url, this.locale);
        this.id = skuToId(this.product.sku);
        this.gridProductId = skuToGridProductId(this.product.sku);
        this.isRetailOnlyStock = hasStockRetailOnly(this.product);
        this.firstAsset = this.getFirstAsset();

        // fix href refresh in SSR (SEO)
        this.changeDetector.detectChanges();
    }

    public ngAfterViewInit(): void {
        this.subscription.add(
            forkJoin({
                menuLevel: this.gridFacade.menuLevel$,
            }).subscribe((facade) => {
                this.menuLevel = facade.menuLevel;
            }),
        );

        this.subscription.add(
            // React to a new grid (e.g. filtered one) to force re-rendering of image-resizer.
            // (APLBLG-130381) lazyload directive did not fired inViewPort event if item in same position after filtering.
            this.gridChange$.subscribe(() => {
                // Avoid to do this in all products (eventually 126)
                if (this.index <= MAX_PRODUCTS_VISIBLE_IN_FIRST_THREE_ROWS) {
                    this.imageResizerContainer.clear();
                    this.imageResizerContainer.createEmbeddedView(
                        this.imageResizer,
                    );
                }
            }),
        );

        // On click, we save the position if index of product < number max of product possibly loaded.
        this.subscription.add(
            fromEvent(this.productItemMeta.nativeElement, "click")
                .pipe(
                    tap((event) => this.onClick(event as MouseEvent)),
                    map(() => getPageSizeValue()),
                    tap(({ maxViewSize }: PageSizeValue) =>
                        this.savePosition(maxViewSize + this.editosDisplayed),
                    ),
                )
                .subscribe(),
        );

        // When the mouse arrives on the result item grid, we display the corresponding arrows control
        this.subscription.add(
            merge(
                fromEvent<Event>(
                    this.imageResizerDivContainer.nativeElement,
                    "mouseenter",
                ),
                fromEvent<Event>(this.productItemMeta.nativeElement, "focus"),
            )
                .pipe(
                    filter(
                        (event) =>
                            event.type !== "focus" ||
                            (event.type === "focus" &&
                                (event as FocusEvent).relatedTarget !== null),
                    ),
                    tap(() => {
                        this.showAlternativesViewsOnDesktop = true;
                        this.shouldShowAlternativeViews =
                            this.shouldDisplayAlternativeViews();

                        if (!this.isMobile) {
                            this.blockPointer = true;
                        }

                        this.changeDetector.detectChanges();
                    }),
                )
                .subscribe(),
        );

        // When the mouse leaves the grid result item, we hide the corresponding arrows control
        this.subscription.add(
            merge(
                fromEvent(this.productItem.nativeElement, "mouseleave"),
                fromEvent(this.productItem.nativeElement, "focusout").pipe(
                    filter((event) => {
                        const targetElement = (
                            event as FocusEvent | MouseEvent
                        )["relatedTarget"] as HTMLElement;
                        // trigger event only if not focusout:
                        // - from parent link
                        // - from carousel-control
                        // - from play-pause-btn
                        return (
                            !targetElement ||
                            (targetElement &&
                                targetElement.id !==
                                    this.productItemMeta.nativeElement.id &&
                                targetElement.id !== "carousel-control" &&
                                targetElement.id !== "play-pause-btn")
                        );
                    }),
                ),
            )
                .pipe(
                    tap(() => {
                        this.showAlternativesViewsOnDesktop = false;
                        this.shouldShowAlternativeViews =
                            this.shouldDisplayAlternativeViews();

                        if (!this.isMobile) {
                            this.blockPointer = false;
                        }

                        this.changeDetector.detectChanges();
                    }),
                )
                .subscribe(),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public getControlPosition(): ControlPosition {
        return this.product.assets.length > 1 &&
            this.product?.isHero !== true &&
            this.isMobile &&
            !this.isListMode
            ? HIGH_POSITION
            : MEDIUM_POSITION;
    }

    public shouldDisplayAlternativeViews(): boolean {
        return (
            // Always display alternatives views for videos on grid
            this.hasVideoAsset() === true ||
            ((this.showAlternativesViewsOnDesktop ||
                (this.showAlternativesViewsOnMobile &&
                    this.carouselInViewPort)) &&
                this.product.assets !== undefined &&
                this.product.assets.length > 1)
        );
    }

    public onClick(event: MouseEvent): void {
        // stop link event
        stopEventPropagation(event);

        this.gridFacade.selectGridItem(this.product, this.index, false);
    }

    public onImageInViewPort(status: boolean): void {
        if (status) {
            this.carouselInViewPort = true;
            this.shouldShowAlternativeViews =
                this.shouldDisplayAlternativeViews();
            this.changeDetector.detectChanges();
        }
        this.productInViewportService.addOrUpdateProductLoadByItemId(
            this.id,
            status ? this.generateProductLoad() : undefined,
        );
    }

    private hasVideoAsset(): boolean {
        return (
            this.product?.assets?.some(
                (gridAsset) => gridAsset.type === AssetMediaType.Video,
            ) ?? false
        );
    }

    private getFirstAsset(): string {
        // First asset when product has a video is next asset
        // If assets has no video, videoIndex equal -1
        let videoIndex =
            this.product?.assets?.findIndex(
                (asset: GridAsset) => asset.type === AssetMediaType.Video,
            ) ?? -1;

        videoIndex++;
        const url =
            this.product?.assets?.[videoIndex]?.url ??
            this.product?.assets?.[0]?.url;

        return url ?? "";
    }

    private savePosition(maxViewSize: number): void {
        // If product located in first three loaded pages, we save its location.
        if (this.index < maxViewSize) {
            const position = this.window.scrollY;
            this.gridFacade.setBackToGridProperties(position, this.product.sku);
        }
    }

    private generateProductLoad(): ProductLoaded {
        const productInfo = {
            index: this.index,
            product: this.product,
        };

        return {
            isSearchGrid: this.isSearchGrid,
            menuLevel: this.menuLevel,
            productInfo,
        };
    }
}
